import Flickity from 'flickity';

// Below this viewport width we leave the carousel as-is (mobile keeps its 2-up peek).
const CENTER_MIN_WIDTH = 768;

// When a panel's products don't fill the row, Flickity left-aligns the cells (data-align
// "left"). On tablet/desktop that leaves an awkward gap on the right (e.g. fewer than 6
// products). Measure the cells against the viewport and toggle `is-centered` so the CSS can
// lay them out as a centred flex row instead. No-op below CENTER_MIN_WIDTH.
const centerIfFits = (sliderEl: HTMLElement) => {
  const viewport = sliderEl.querySelector<HTMLElement>('.flickity-viewport');
  const cells = Array.from(sliderEl.querySelectorAll<HTMLElement>('.js-slide'));
  if (!viewport || !cells.length) return;
  const total = cells.reduce((sum, cell) => sum + cell.getBoundingClientRect().width, 0);
  // +1px tolerance for sub-pixel rounding.
  const fits = window.innerWidth >= CENTER_MIN_WIDTH && total <= viewport.clientWidth + 1;
  sliderEl.classList.toggle('is-centered', fits);
};

// Wire the optional selector (collection-tabs) scroll indicator. Reads the type from
// `.js-pgcc-selector[data-pagination]` ("line" | "dots" | "none") and reflects the tabs'
// horizontal scroll. Everything hides when the tabs don't overflow.
const setupSelectorPagination = (carousel: HTMLElement) => {
  const selector = carousel.querySelector<HTMLElement>('.js-pgcc-selector');
  const tabsEl = carousel.querySelector<HTMLElement>('.js-pgcc-tabs');
  if (!selector || !tabsEl) return;

  const type = selector.dataset.pagination;
  // All three variants (line / dots / multiple-line) share the same page-segment logic;
  // only the CSS (via the parent variant class) differs: an expanding navy active line,
  // round dots, or short navy lines respectively.
  if (type !== 'line' && type !== 'dots' && type !== 'multiple-line') return;

  const maxScroll = () => tabsEl.scrollWidth - tabsEl.clientWidth;
  const hasOverflow = () => maxScroll() > 1;

  const dotsWrap = selector.querySelector<HTMLElement>('.js-pgcc-selector-dots');
  if (!dotsWrap) return;

  const pageCount = () => Math.max(1, Math.ceil(tabsEl.scrollWidth / tabsEl.clientWidth));

  // Map scroll position (0…maxScroll) evenly across the page indices so the last page is
  // reachable even when the final "page" is less than a full viewport wide — otherwise
  // round(scrollLeft / clientWidth) rounds the last short page back down to the first.
  const currentPage = () => {
    const max = maxScroll();
    const pages = pageCount();
    if (max <= 0 || pages <= 1) return 0;
    return Math.round((tabsEl.scrollLeft / max) * (pages - 1));
  };

  const scrollToPage = (i: number) => {
    const max = maxScroll();
    const pages = pageCount();
    const left = pages > 1 ? (i / (pages - 1)) * max : 0;
    tabsEl.scrollTo({ left, behavior: 'smooth' });
  };

  const updateDots = () => {
    const page = currentPage();
    Array.from(dotsWrap.children).forEach((d, i) => {
      d.classList.toggle('is-selected', i === page);
    });
  };

  const buildDots = () => {
    const overflow = hasOverflow();
    dotsWrap.hidden = !overflow;
    if (!overflow) { dotsWrap.replaceChildren(); return; }
    const pages = pageCount();
    if (dotsWrap.children.length !== pages) {
      dotsWrap.replaceChildren();
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pg-collection-carousel__selector-dot';
        dot.setAttribute('aria-label', `${i + 1}`);
        dot.addEventListener('click', () => scrollToPage(i));
        dotsWrap.appendChild(dot);
      }
    }
    updateDots();
  };

  tabsEl.addEventListener('scroll', updateDots, { passive: true });
  window.addEventListener('resize', buildDots);
  // Run after layout settles (pills/fonts, and the tabs may start hidden in a panel).
  requestAnimationFrame(buildDots);
  setTimeout(buildDots, 300);
};

// Product Grid Collection Carousel: a row of tabs (one per Shopify collection) that swap
// which product carousel + CTA is shown. The carousels themselves are initialised by the
// shared slider.ts (.js-slider). Inactive panels are display:none, so when a panel is shown
// we must call Flickity.resize() — Flickity can't measure a hidden element on init.
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll<HTMLElement>('.pg-collection-carousel');

  carousels.forEach((carousel) => {
    const tabs = Array.from(carousel.querySelectorAll<HTMLElement>('.pg-collection-carousel__tab'));
    const panels = Array.from(carousel.querySelectorAll<HTMLElement>('.pg-collection-carousel__panel'));
    if (!panels.length) return;

    const getActiveSlider = () =>
      carousel.querySelector<HTMLElement>('.pg-collection-carousel__panel.is-active .js-slider');

    const showPanel = (panel: HTMLElement) => {
      const sliderEl = panel.querySelector<HTMLElement>('.js-slider');
      if (!sliderEl) return;
      const flkty = Flickity.data(sliderEl);
      if (!flkty) return;

      // Panels other than the first are display:none when Flickity initialises, so their
      // cells/groups measured 0 width — the carousel renders blank and (because the cells
      // were grouped into a single slide) dragging is disabled (draggable '>1').
      const relayout = () => {
        // Drop any centring override so Flickity re-measures in its own (absolute) layout.
        sliderEl.classList.remove('is-centered');
        // Rebuild cells + slides from the now-visible DOM, recompute sizes, then re-evaluate
        // draggability against the rebuilt slide groups — otherwise the carousel won't swipe.
        flkty.reloadCells();
        flkty.resize();
        flkty.updateDraggable();
        flkty.select(0, false, true);
        window.dispatchEvent(new Event('resize'));
        centerIfFits(sliderEl);
      };

      // Once for the immediate layout, and again after layout/lazyloaded images settle
      // (hidden → visible needs a tick for measurements to be correct).
      requestAnimationFrame(relayout);
      setTimeout(relayout, 300);
    };

    const activate = (index: string) => {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.index === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      panels.forEach((panel) => {
        const isActive = panel.dataset.index === index;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          showPanel(panel);
        }
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (tab.dataset.index) {
          activate(tab.dataset.index);
        }
      });
    });

    // ── Selector scroll pagination (Line / Dots) ─────────────────────────────
    // The tabs row scrolls horizontally when the pills overflow. Reflect that scroll
    // with a progress line (above) or dots (below). Both hide when the tabs fit.
    setupSelectorPagination(carousel);

    // Centre the panel shown on load (Flickity is initialised by slider.ts on DOMContentLoaded;
    // run on the next frame so its viewport/cells exist before we measure).
    requestAnimationFrame(() => {
      const sliderEl = getActiveSlider();
      if (sliderEl) centerIfFits(sliderEl);
    });

    // Re-evaluate on resize for the visible panel (a desktop width change can flip whether
    // the cards fit).
    let raf = 0;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const sliderEl = getActiveSlider();
        if (sliderEl) centerIfFits(sliderEl);
      });
    });
  });
});
